import { flatten, uniqBy } from "ramda";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import type { Organism, Parent, PlaybookEvent, Sample } from "../../../graphql.js";
import { type IAppState } from "../../../redux/reducer.js";
import { UNKNOWN_GENOME_ID } from "../../../settings/settings.js";
import { query as Q } from "@/app-utils/graphql/query.ts";
import {
  GetFileSuggestionsContextDocument,
  type GetFileSuggestionsContextQuery,
  type GetFileSuggestionsContextQueryVariables,
} from "@/modules/files/graphql/files-queries.gql.js";
import type { FileSuggestionsContextProps } from "@/modules/files/stores/file-search.js";
import { useBamFileIds, useBedFileIds } from "@/ui-modules/genome-viewer/hooks/useSequences.ts";

const query = Q<GetFileSuggestionsContextQuery, GetFileSuggestionsContextQueryVariables>(
  GetFileSuggestionsContextDocument,
);
export const useGenomeViewerFileSuggestionsContext = () => {
  const configuredBamFileIds: number[] = useBamFileIds() ?? [];
  const configuredBedFileIds: number[] = useBedFileIds() ?? [];
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);

  const getGenomeViewerFileSuggestionsContext = useCallback(async (): Promise<FileSuggestionsContextProps> => {
    const genomeBrowserFiles: number[] = configuredBamFileIds.concat(configuredBedFileIds);

    const { data } = await query({ fileIds: genomeBrowserFiles });

    const samples: Sample[] = uniqBy<Sample, number>(
      (sample) => sample.id,
      flatten(data.projectFilesById.map((file) => file.samples)),
    );
    const parents: Parent[] = uniqBy<Parent, number>(
      (parent) => parent.id,
      flatten(data.projectFilesById.map((file) => file.parents)),
    );
    const events: PlaybookEvent[] = uniqBy<PlaybookEvent, number>(
      (event) => event.id,
      flatten(data.projectFilesById.map((file) => file.playbookEvents)),
    );
    const organisms: Organism[] = uniqBy<Organism, number>(
      (organism) => organism.id,
      flatten(
        data.projectFilesById.map((file) => file.organisms).concat(genome.id !== UNKNOWN_GENOME_ID ? [genome] : []),
      ),
    );
    return {
      organisms: organisms,
      samples: samples,
      parents: parents,
      events: events,
    };
  }, [configuredBamFileIds, configuredBedFileIds, genome]);
  return getGenomeViewerFileSuggestionsContext;
};
